'use client'

import { Phone } from 'lucide-react'
import * as React from 'react'
import { FormLabel } from './form-label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface PhoneInputProps {
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  name?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  error?: string
  label?: string
  required?: boolean
}

// Format phone number as user types
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits (North American format)
  const limitedDigits = digits.slice(0, 10)

  // Format based on length
  if (limitedDigits.length <= 3) {
    return limitedDigits
  } else if (limitedDigits.length <= 6) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
  }
}

// Get cursor position after formatting
const getCursorPosition = (
  prevValue: string,
  newValue: string,
  cursorPos: number,
): number => {
  const prevDigits = prevValue.replace(/\D/g, '').length
  const newDigits = newValue.replace(/\D/g, '').length

  // If digits were added, move cursor forward
  if (newDigits > prevDigits) {
    // Count dashes before cursor position in new value
    const dashesBeforeCursor =
      newValue.slice(0, cursorPos).split('-').length - 1
    return cursorPos + (dashesBeforeCursor > 0 ? 1 : 0)
  }

  return cursorPos
}

export function PhoneInput({
  value = '',
  onChange,
  onBlur,
  name,
  placeholder = '555-123-4567',
  className,
  disabled = false,
  error,
  label,
  required = false,
}: PhoneInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [displayValue, setDisplayValue] = React.useState(
    formatPhoneNumber(value),
  )
  const [hasBlurred, setHasBlurred] = React.useState(false)

  // Update display value when prop value changes
  React.useEffect(() => {
    const formatted = formatPhoneNumber(value)
    setDisplayValue(formatted)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPosition = e.target.selectionStart || 0
    const prevValue = displayValue

    // Format the new value
    const formatted = formatPhoneNumber(newValue)
    setDisplayValue(formatted)

    // Call onChange with the formatted value
    onChange?.(formatted)

    // Set cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = getCursorPosition(
          prevValue,
          formatted,
          cursorPosition,
        )
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos)
      }
    }, 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key

    // Always allow navigation and control keys
    const navigationKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]
    if (navigationKeys.includes(key)) return

    // Allow common clipboard/select shortcuts (Ctrl/Cmd + A/C/V/X)
    if (
      (e.ctrlKey || e.metaKey) &&
      ['a', 'c', 'v', 'x', 'A', 'C', 'V', 'X'].includes(key)
    )
      return

    // Allow digits only
    const isDigit = /^[0-9]$/.test(key)
    if (!isDigit) {
      e.preventDefault()
      return
    }

    // Enforce max 10 digits
    const digits = displayValue.replace(/\D/g, '')
    if (digits.length >= 10) {
      e.preventDefault()
      return
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const formatted = formatPhoneNumber(pastedText)
    setDisplayValue(formatted)
    onChange?.(formatted)
  }

  const handleBlur = () => {
    setHasBlurred(true)
    onBlur?.()
  }

  // Validate format and show visual feedback
  const isValidFormat = React.useMemo(() => {
    if (!displayValue) return true // Empty is valid (optional field)
    const digits = displayValue.replace(/\D/g, '')
    return digits.length === 10
  }, [displayValue])

  const isEmpty = !displayValue || displayValue.trim().length === 0
  const showInternalInvalid =
    required && hasBlurred && !error && (isEmpty || !isValidFormat)

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <FormLabel htmlFor={name} required={required}>
          {label}
        </FormLabel>
      )}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <Phone className="h-4 w-4 text-muted-foreground" />
        </div>
        <Input
          ref={inputRef}
          type="tel"
          id={name}
          name={name}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={hasBlurred ? '' : placeholder}
          disabled={disabled}
          aria-required={required}
          className="pl-10"
        />
      </div>
      {error && <div className="text-xs text-destructive">{error}</div>}
      {showInternalInvalid && (
        <div className="text-xs text-destructive">
          {isEmpty
            ? 'Phone is required.'
            : 'Phone number must be 10 digits (e.g., 555-123-4567)'}
        </div>
      )}
    </div>
  )
}
